<template>
  <!-- ChatAI doesn't render anything visual - it just handles the AI message logic -->
</template>

<script>
import { triggerAIResponse } from '../../../lib/aiService'
import { chatStateManager } from '../../../lib/chatStateManager'
import { loadAIPreferences, getDefaultAIPreferences } from '../../../lib/AIPreferences';

// Global singleton management with better state tracking
let activeChatAIInstance = null;
let globalProcessedMessageIds = new Set();
let globalHasAddedGreeting = false;
let globalHasEscalated = false;

export default {
  name: 'ChatAI',
  props: {
    styles: {
      type: Object,
      default: () => ({
        buttonColor: '#000000'
      })
    },
    customerName: {
      type: String,
      default: ''
    },
    customer: {
      type: Object,
      default: null
    },
    conversation: {
      type: Object,
      default: null
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  emits: ['add-message', 'remove-typing', 'escalate-to-human', 'phase-complete'],
  data() {
    return {
      instanceId: `ai-${Date.now()}-${Math.random()}`,
      messageTimeout: null,
      isActiveInstance: false,
      aiPreferences: null
    }
  },
  computed: {
    // Use global state for these critical flags
    hasAddedGreeting() {
      return globalHasAddedGreeting;
    },
    hasEscalated() {
      return globalHasEscalated;
    },
    processedMessageIds() {
      return globalProcessedMessageIds;
    },
    isProcessingAI() {
      return chatStateManager.getState().isAIProcessing;
    }
  },
  mounted() {
    // Singleton pattern - only one ChatAI can be active
    if (activeChatAIInstance && activeChatAIInstance !== this.instanceId) {
      console.log(`🚫 ChatAI [${this.instanceId}] BLOCKED - Instance [${activeChatAIInstance}] already active`);
      return;
    }
    
    activeChatAIInstance = this.instanceId;
    this.isActiveInstance = true;
    
    console.log(`🤖 ChatAI [${this.instanceId}] ACTIVATED as singleton instance`);
    console.log(`🤖 ChatAI [${this.instanceId}] mounted with:`, { 
      customer: this.customer, 
      conversation: this.conversation,
      messageCount: this.messages.length,
      globalGreeting: globalHasAddedGreeting,
      globalEscalated: globalHasEscalated,
      globalProcessedIds: globalProcessedMessageIds.size
    });
    
    // Reset global state if this is a completely new chat (no conversation)
    if (!this.conversation) {
      console.log(`🔄 ChatAI [${this.instanceId}] New chat detected - resetting global state`);
      this.resetGlobalState();
    }
    
    this.checkAndStartAISequence();
  },
  beforeUnmount() {
    console.log(`🤖 ChatAI [${this.instanceId}] unmounting`);
    
    // Release singleton lock if this is the active instance
    if (activeChatAIInstance === this.instanceId) {
      activeChatAIInstance = null;
      console.log(`🤖 ChatAI [${this.instanceId}] released singleton lock`);
    }
    
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
    
    // Clear global processing flag when unmounting
    chatStateManager.updateState({ isAIProcessing: false });
  },
  watch: {
    messages: {
      handler(newMessages, oldMessages) {
        // Only process if this is the active singleton instance
        if (!this.isActiveInstance || activeChatAIInstance !== this.instanceId) {
          return;
        }
        
        console.log(`🤖 ChatAI [${this.instanceId}] messages watcher triggered:`, { 
          newCount: newMessages.length, 
          oldCount: oldMessages?.length || 0 
        });
        
        // Clear any existing timeout to debounce
        if (this.messageTimeout) {
          clearTimeout(this.messageTimeout);
        }
        
        // Debounce message processing by 300ms
        this.messageTimeout = setTimeout(() => {
          this.$nextTick(() => {
            this.checkForNewCustomerMessages(newMessages);
          });
        }, 300);
      },
      deep: true,
      immediate: false
    },
    
    conversation(newConversation) {
      if (newConversation && this.isActiveInstance) {
        console.log(`🤖 ChatAI [${this.instanceId}]: Conversation changed, ready for messages`);
      }
    }
  },
  methods: {
    // Reset global state for new chats
    resetGlobalState() {
      globalProcessedMessageIds.clear();
      globalHasAddedGreeting = false;
      globalHasEscalated = false;
      console.log(`🔄 ChatAI [${this.instanceId}] Global state reset`);
    },

    checkAndStartAISequence() {
      if (!this.isActiveInstance) return;
      
      // ✅ FIXED: Better greeting detection - look for ANY AI message that looks like a greeting
      const hasAIGreeting = this.messages.some(msg => 
        msg.type === 'ai' && 
        msg.content && 
        !msg.isTyping &&
        (msg.content.includes('Wie kann ich dir heute') || 
         msg.content.includes('helfen') ||
         msg.content.includes('NEX AI') ||
         msg.content.includes('Hallo') ||
         msg.content.includes('Guten Tag') ||
         msg.content.includes('KI-Assistent') ||
         msg.content.includes('behilflich') ||
         msg.content.includes('Persönlicher'))
      );
      
      if (hasAIGreeting) {
        console.log(`🤖 ChatAI [${this.instanceId}] AI greeting already exists in messages, marking as added`);
        globalHasAddedGreeting = true;
        return;
      }
      
      // ✅ FIXED: Check global flag but only if there are actual messages
      // If global flag is set but no messages exist, we need to add the greeting
      if (globalHasAddedGreeting && this.messages.length > 0) {
        console.log(`🤖 ChatAI [${this.instanceId}] Greeting already added globally and messages exist, skipping...`);
        return;
      }
      
      // ✅ If global flag is set but no messages, reset the flag and add greeting
      if (globalHasAddedGreeting && this.messages.length === 0) {
        console.log(`🤖 ChatAI [${this.instanceId}] Global flag set but no messages - messages were cleared, resetting flag and adding greeting`);
        globalHasAddedGreeting = false;
      }
      
      console.log(`🤖 ChatAI [${this.instanceId}] No greeting found, starting AI sequence`);
      this.startAISequence();
    },
    
    async startAISequence() {
      if (!globalHasAddedGreeting && this.isActiveInstance) {
        globalHasAddedGreeting = true;
        
        await this.loadAIPreferencesForGreeting();
        this.addAIGreeting();
      }
    },
    
    async loadAIPreferencesForGreeting() {
      if (!this.customer?.companyId) {
        console.log(`⚠️ ChatAI [${this.instanceId}] No companyId for preferences, using default greeting`);
        return;
      }

      try {
        console.log(`🎨 ChatAI [${this.instanceId}] Loading AI preferences for company: ${this.customer.companyId}`);
        
        const preferences = await loadAIPreferences(this.customer.companyId);
        
        if (preferences) {
          this.aiPreferences = preferences;
          console.log(`✅ ChatAI [${this.instanceId}] AI preferences loaded:`, {
            style: preferences.personalityStyle,
            hasCustomGreeting: !!preferences.greetingMessage,
            greetingPreview: preferences.greetingMessage?.substring(0, 50) + '...'
          });
        } else {
          this.aiPreferences = getDefaultAIPreferences();
          console.log(`📝 ChatAI [${this.instanceId}] Using default preferences`);
        }
      } catch (error) {
        console.error(`❌ ChatAI [${this.instanceId}] Error loading AI preferences:`, error);
        this.aiPreferences = getDefaultAIPreferences();
      }
    },
    
    addAIGreeting() {
      if (!this.isActiveInstance) return;
      
      // ✅ FIXED: Add temporary typing indicator
      chatStateManager.addTypingIndicator({ type: 'ai' });
      
      // After 1.5s, remove typing and show actual greeting
      setTimeout(() => {
        chatStateManager.removeTypingMessage();
        
        const greetingMessage = this.buildGreetingMessage();
        
        // Add the actual AI greeting message
        this.$emit('add-message', {
          type: 'ai',
          content: greetingMessage
        });
        
        console.log(`💬 ChatAI [${this.instanceId}] Dynamic greeting sent:`, greetingMessage.substring(0, 50) + '...');
      }, 1500);
    },

    buildGreetingMessage() {
      // Default fallback greeting
      const defaultGreeting = `Hey! Mein Name ist NEX AI, ich bin dein Persönlicher KI Assistent!\nWie kann ich dir heute behilflich sein${this.customerName ? ', ' + this.customerName : ''}?`;
      
      // If no preferences loaded, use default
      if (!this.aiPreferences) {
        console.log(`📝 ChatAI [${this.instanceId}] No preferences, using default greeting`);
        return defaultGreeting;
      }
      
      // Check if there's a custom greeting message
      const customGreeting = this.aiPreferences.greetingMessage;
      
      if (customGreeting && customGreeting.trim()) {
        let personalizedGreeting = customGreeting;
        
        // Add customer name if available and greeting doesn't already include it
        if (this.customerName && !customGreeting.toLowerCase().includes(this.customerName.toLowerCase())) {
          if (customGreeting.includes('!')) {
            personalizedGreeting = customGreeting.replace(/!/, `, ${this.customerName}!`);
          } else if (customGreeting.includes('?')) {
            personalizedGreeting = customGreeting.replace(/\?/, `, ${this.customerName}?`);
          } else {
            personalizedGreeting = `${customGreeting}, ${this.customerName}`;
          }
        }
        
        console.log(`🎨 ChatAI [${this.instanceId}] Using custom greeting:`, personalizedGreeting.substring(0, 50) + '...');
        return personalizedGreeting;
      }
      
      // If no custom greeting, use default but personalize it based on personality style
      const personalityStyle = this.aiPreferences.personalityStyle || 'friendly';
      
      const personalizedGreetings = {
        professional: `Guten Tag${this.customerName ? ', ' + this.customerName : ''}! Ich bin Ihr KI-Assistent und helfe Ihnen gerne bei Ihren Anfragen.`,
        friendly: `Hallo${this.customerName ? ', ' + this.customerName : ''}! Ich bin Ihr freundlicher KI-Assistent. Womit kann ich Ihnen heute helfen? 😊`,
        casual: `Hey${this.customerName ? ', ' + this.customerName : ''}! Was ist los? Ich bin hier, um zu helfen! 👋`,
        formal: `Guten Tag${this.customerName ? ', ' + this.customerName : ''}. Ich bin Ihr KI-Assistent und freue mich, Ihnen heute behilflich zu sein.`
      };
      
      const personalizedGreeting = personalizedGreetings[personalityStyle] || personalizedGreetings.friendly;
      
      console.log(`🎭 ChatAI [${this.instanceId}] Using ${personalityStyle} style greeting:`, personalizedGreeting.substring(0, 50) + '...');
      return personalizedGreeting;
    },

    checkForNewCustomerMessages(messages) {
      // Only the active singleton instance should process messages
      if (!this.isActiveInstance || activeChatAIInstance !== this.instanceId) {
        console.log(`🚫 ChatAI [${this.instanceId}] Skipping: Not the active singleton instance`);
        return;
      }

      console.log(`🔍 ChatAI [${this.instanceId}] checking for new customer messages...`, {
        hasCustomer: !!this.customer,
        hasConversation: !!this.conversation,
        isProcessing: this.isProcessingAI,
        hasEscalated: this.hasEscalated,
        messageCount: messages.length
      });

      if (!this.customer || !this.conversation) {
        console.log(`🔄 ChatAI [${this.instanceId}] Skipping: Missing customer or conversation`);
        return;
      }

      if (this.isProcessingAI) {
        console.log(`🔄 ChatAI [${this.instanceId}] Skipping: Already processing`);
        return;
      }

      if (this.hasEscalated) {
        console.log(`🔄 ChatAI [${this.instanceId}] Skipping: Already escalated`);
        return;
      }

      // Use global state to check if ANY instance is processing
      const globalState = chatStateManager.getState();
      if (globalState.isAIProcessing) {
        console.log(`🔄 ChatAI [${this.instanceId}] Skipping: Global AI processing active`);
        return;
      }

        // Find customer messages that we haven't processed yet (using global tracking)
        const unprocessedCustomerMessages = messages.filter(msg => 
          msg.type === 'customer' && 
          !msg.isTyping && 
          msg.content && 
          msg.content.trim() !== '' &&
          !globalProcessedMessageIds.has(msg.id)
        );

        if (unprocessedCustomerMessages.length > 0) {
          const latestMessage = unprocessedCustomerMessages[unprocessedCustomerMessages.length - 1];
          console.log(`🤖 ChatAI [${this.instanceId}] NEW CUSTOMER MESSAGE:`, latestMessage.content);
          console.log(`🔥 ChatAI [${this.instanceId}] Triggering AI response for message ID:`, latestMessage.id);
          
          // Set global processing flag
          chatStateManager.updateState({ isAIProcessing: true });
          
          // Mark this message as processed globally
          globalProcessedMessageIds.add(latestMessage.id);
          
          // Process the message
          this.handleCustomerMessage(latestMessage.content, latestMessage.id);
        } else {
          console.log(`🤖 ChatAI [${this.instanceId}] No new customer messages to process`);
        }
    },

    // ✅ NEW: Enhanced validation for chat ending
    validateChatEnd(customerMessage, aiResult) {
      console.log('🔍 Validating chat end for message:', customerMessage);
      
      // ✅ STRICT: Only end chat for explicit goodbye phrases
      const explicitGoodbyes = [
        'tschüss', 'tschüß', 'auf wiedersehen', 'bis bald', 
        'goodbye', 'bye', 'ciao', 'bis dann',
        'das wars', 'das war alles', 'ich bin fertig',
        'chat beenden', 'chat schließen', 'ende'
      ];
      
      const messageLower = customerMessage.toLowerCase()
        .replace(/ß/g, 'ss')
        .replace(/ü/g, 'ue')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe');
      
      const isExplicitGoodbye = explicitGoodbyes.some(goodbye => 
        messageLower.includes(goodbye.toLowerCase())
      );
      
      // ✅ CRITICAL: Never end chat if customer is asking questions
      const questionIndicators = ['?', 'wie', 'was', 'wann', 'wo', 'warum', 'wieso', 'weshalb', 
                                  'kosten', 'preis', 'price', 'cost', 'mehr', 'details', 'info',
                                  'kannst du', 'können sie', 'can you', 'how much', 'tell me'];
      
      const hasQuestion = questionIndicators.some(indicator => 
        messageLower.includes(indicator.toLowerCase())
      );
      
      if (hasQuestion) {
        console.log('❌ NOT ending chat - customer has questions:', customerMessage);
        return false;
      }
      
      // ✅ Don't end if customer seems engaged or wants more info
      const engagementWords = ['ja', 'okay', 'danke', 'interessant', 'gut', 'toll', 'mehr',
                               'yes', 'ok', 'thanks', 'interesting', 'good', 'great', 'more'];
      
      const seemsEngaged = engagementWords.some(word => 
        messageLower.includes(word.toLowerCase())
      ) && customerMessage.length > 5;
      
      if (seemsEngaged && !isExplicitGoodbye) {
        console.log('❌ NOT ending chat - customer seems engaged:', customerMessage);
        return false;
      }
      
      // ✅ Only end if explicit goodbye OR very clear ending intent
      const shouldEnd = isExplicitGoodbye;
      
      console.log('🎯 Chat end validation result:', {
        isExplicitGoodbye,
        hasQuestion,
        seemsEngaged,
        shouldEnd,
        message: customerMessage.substring(0, 50)
      });
      
      return shouldEnd;
    },

// ChatAI.vue - Updated handleCustomerMessage method
// REPLACE your existing handleCustomerMessage method with this:

async handleCustomerMessage(messageContent, messageId) {
  // ✅ ENHANCED: Better validation and debugging
  const validationData = {
    isActiveInstance: this.isActiveInstance,
    activeChatAIInstance,
    currentInstanceId: this.instanceId,
    isProcessingAI: this.isProcessingAI,
    globalHasEscalated,
    hasCustomer: !!this.customer,
    hasConversation: !!this.conversation
  };
  
  console.log(`🔍 ChatAI [${this.instanceId}] handleCustomerMessage validation:`, validationData);
  
  if (!this.isActiveInstance || activeChatAIInstance !== this.instanceId) {
    console.log(`⏳ ChatAI [${this.instanceId}] Skipping: Not the active singleton instance`);
    return;
  }
  
  if (globalHasEscalated) {
    console.log(`⏳ ChatAI [${this.instanceId}] Skipping: Already escalated`);
    return;
  }
  
  if (!this.customer || !this.conversation) {
    console.log(`⏳ ChatAI [${this.instanceId}] Skipping: Missing customer or conversation`);
    return;
  }
  
  // ✅ FIXED: Check and clear any stuck processing flags
  if (this.isProcessingAI) {
    console.log(`⚠️ ChatAI [${this.instanceId}] AI processing flag is stuck - clearing it`);
    chatStateManager.clearAIProcessing();
    // Small delay to ensure state update propagates
    await this.delay(100);
  }
  
  // ✅ Double-check after clearing
  if (this.isProcessingAI) {
    console.log(`⏳ ChatAI [${this.instanceId}] Still processing after clear attempt - skipping`);
    return;
  }
  
  // Set global processing flag
  chatStateManager.setAIProcessing(true);
  console.log(`🤖 ChatAI [${this.instanceId}] PROCESSING:`, messageContent);

  try {
    // 1. ✅ FIXED: Show AI typing indicator
    console.log(`💭 ChatAI [${this.instanceId}] Showing typing indicator...`);
    chatStateManager.addTypingIndicator({ type: 'ai' });

    // 2. Small delay for better UX
    await this.delay(1200);

    // 3. Call triggerAIResponse
    console.log(`🔮 ChatAI [${this.instanceId}] Calling triggerAIResponse with messageId:`, messageId);
    const aiResult = await triggerAIResponse(messageContent, messageId);

    console.log(`🎯 ChatAI [${this.instanceId}] AI Result:`, aiResult);

    // 4. ✅ FIXED: Remove typing indicator
    chatStateManager.removeTypingMessage();

    // 5. Handle AI response
    if (aiResult.success && aiResult.aiMessage) {
      console.log(`✅ ChatAI [${this.instanceId}] AI response successful`);
      
      console.log(`📡 ChatAI [${this.instanceId}] Message saved to Appwrite, real-time subscription will add it to UI`);

      // ✅ NEW: Handle confirmation flow
      if (aiResult.awaitingConfirmation) {
        console.log('🔔 AI is awaiting chat end confirmation - no further action needed');
        // Just wait for user's response to the confirmation question
        return;
      }

      // ✅ Handle chat ending (only after confirmation)
      if (aiResult.chatEnded) {
        console.log('🏁 Chat ended after user confirmation - transitioning to rating');
        
        setTimeout(() => {
          console.log(`🚀 ChatAI [${this.instanceId}] Emitting phase-complete to end`);
          this.$emit('phase-complete', {
            nextPhase: 'end',
            reason: 'User confirmed chat end'
          });
        }, 2000);
        
        return;
      }

      // 6. Handle escalation if needed (ONLY ONCE using global flag)
      if (aiResult.requiresEscalation && !globalHasEscalated) {
        console.log(`🚨 ChatAI [${this.instanceId}] AI requesting escalation`);
        globalHasEscalated = true;
        
        // Wait a moment then trigger escalation
        setTimeout(() => {
          console.log(`🚀 ChatAI [${this.instanceId}] Emitting escalate-to-human`);
          this.$emit('escalate-to-human', {
            reason: 'AI determined human assistance needed',
            confidence: aiResult.aiMessage.ai_confidence
          });
        }, 1500);
      }
    } else {
      // Handle AI failure (ONLY ONCE using global flag)
      if (!globalHasEscalated) {
        console.log(`❌ ChatAI [${this.instanceId}] AI failed, escalating`);
        globalHasEscalated = true;
        
        // Fallback message on AI error
        this.$emit('add-message', {
          type: 'ai',
          content: "Entschuldigung, ich habe gerade technische Probleme. Lass mich einen menschlichen Mitarbeiter für dich holen! 😊"
        });

        // Escalate due to AI failure
        setTimeout(() => {
          console.log(`🚀 ChatAI [${this.instanceId}] Emitting escalate due to failure`);
          this.$emit('escalate-to-human', {
            reason: 'AI system failure',
            error: aiResult.error
          });
        }, 2000);
      }
    }

  } catch (error) {
    console.error(`❌ ChatAI [${this.instanceId}] Error:`, error);
    
    // ✅ FIXED: Remove typing indicator
    chatStateManager.removeTypingMessage();
  
    // Handle exception (ONLY ONCE using global flag)
    if (!globalHasEscalated) {
      globalHasEscalated = true;
      
      // Show error message
      this.$emit('add-message', {
        type: 'ai',
        content: "Es tut mir leid, ich habe gerade ein Problem. Lass mich einen Menschen für dich holen!"
      });

      // Escalate due to exception
      setTimeout(() => {
        console.log(`🚀 ChatAI [${this.instanceId}] Emitting escalate due to exception`);
        this.$emit('escalate-to-human', {
          reason: 'AI exception',
          error: error.message
        });
      }, 2000);
    }
  } finally {
    // ✅ FIXED: Use the dedicated clear method
    chatStateManager.clearAIProcessing();
    console.log(`🏁 ChatAI [${this.instanceId}] Processing completed - AI flag cleared`);
  }
},

    // Utility delay function
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }
}
</script>
